import { Trans, plural } from "@lingui/macro";
import { Check } from "@winamp/icons/Check.js";
import { PrivateImg } from "@winamp/media-upload/BytescaleContext/PrivateImg.js";
import { CoverPlaceholder } from "@winamp/ui/components/CoverPlaceholder/CoverPlaceholder.js";
import {
  type ISearchListboxProps,
  SearchListbox,
} from "@winamp/ui/components/SearchListbox/SearchListbox.js";
import { SearchListboxOption } from "@winamp/ui/components/SearchListbox/SearchListboxOption.js";
import { useState } from "react";
import { ServiceCandidateIssue } from "~/components/ServiceCandidateIssue";
import type { ReleaseCandidateInList } from "~/models/release-candidate";
import { canCreateService } from "~/models/service-issue";

type IReleaseCandidatesSelectionProps = ISearchListboxProps<false> & {
  candidates: Array<ReleaseCandidateInList>;
};

export function ReleaseCandidatesSelection({
  candidates,
  ...props
}: IReleaseCandidatesSelectionProps) {
  const [query, setQuery] = useState("");

  const filteredCandidates = candidates.filter((candidate) =>
    candidate.title.toLowerCase().includes(query.toLowerCase()),
  );

  return (
    <SearchListbox query={query} setQuery={setQuery} {...props}>
      {filteredCandidates?.map((candidate) => (
        <CandidateSelectionRow
          candidate={candidate}
          key={candidate.audioUid ?? candidate.collectionUid ?? ""}
        />
      ))}
      {filteredCandidates?.length === 0 && (
        <span className="text-sm/5 font-normal px-4">
          <Trans>No items could be found.</Trans>
        </span>
      )}
    </SearchListbox>
  );
}

interface ICandidateSelectionRowProps {
  candidate: ReleaseCandidateInList;
}

function CandidateSelectionRow({ candidate }: ICandidateSelectionRowProps) {
  const canCreateRelease = canCreateService(candidate.entries);

  return (
    <SearchListboxOption
      value={candidate.audioUid ?? candidate.collectionUid ?? ""}
      disabled={!canCreateRelease}
    >
      {({ selected }) => (
        <>
          <PrivateImg
            src={candidate.coverImagePath}
            alt={`Cover of the candidate ${candidate.title}`}
            className="w-10 h-10 rounded-md"
            options={{ transformationParams: { w: 64, h: 64 } }}
          >
            <CoverPlaceholder className="w-10 h-10 rounded-md" />
          </PrivateImg>
          <div className="flex flex-col w-full">
            <div className="text-primary text-sm font-medium">
              {candidate.title}
            </div>
            <ServiceCandidateIssue
              candidate={candidate}
              additionalChecks={{ canSubmit: !!candidate.coverImagePath }}
            />
          </div>
          {!!candidate.collectionUid && (
            <span className="min-w-fit">
              <Trans>
                {plural(candidate.entries.length, {
                  one: "# audio",
                  other: "# audios",
                })}
              </Trans>
            </span>
          )}
          {selected && <Check className="size-6 shrink-0" />}
        </>
      )}
    </SearchListboxOption>
  );
}
